// import { useCallback, useEffect, useState } from "react";
// import { useDropzone } from "react-dropzone";
// import { FaRegFileLines, FaRegTrashCan } from "react-icons/fa6";
// import { IoImageOutline } from "react-icons/io5";
// import { toast } from "sonner";
// import { ToolTipEllipsis } from "./tooltip-elipsis";
// interface FileType {
//   name: string;
//   size: string;
//   file: File; // Store the original file for further use
// }
// interface Props {
//   onChange: (files: FileList | null) => void;
//   defaultValue: FileList | null;
//   name: string;
//   value: FileList | null;
// }
// export function DropZone({ onChange, defaultValue, name, value }: Props) {
//   const [files, setFiles] = useState<FileType[]>([]);
//   const updateOnChange = useCallback(
//     (updatedFiles: FileType[]) => {
//       const dataTransfer = new DataTransfer();
//       updatedFiles.forEach((file) => dataTransfer.items.add(file.file));
//       onChange(dataTransfer.files);
//     },
//     [onChange], // Dependencies for updateOnChange
//   );
//   const onDrop = useCallback(
//     (acceptedFiles: File[]) => {
//       const newFiles: { name: string; size: string; file: File }[] = [];
//       acceptedFiles.forEach((file) => {
//         if (!files.find((oldFile) => file.name === oldFile.name)) {
//           newFiles.push({
//             name: file.name,
//             size: `${(file.size / 1024).toFixed(2)}KB`,
//             file,
//           });
//         } else {
//           toast.info("There already exists a file with this name");
//         }
//       });
//       const mergedFiles = [...files, ...newFiles];
//       setFiles(mergedFiles);
//       // Use the stable updateOnChange function
//       updateOnChange(mergedFiles);
//     },
//     [files, updateOnChange], // Dependencies for onDrop
//   );
//   useEffect(() => {
//     if (Array.from(value || []).length > 0) {
//       const uploadedFiles = Array.from(value || []).map((file) => ({
//         name: file.name,
//         size: "",
//         file,
//       }));
//       setFiles(uploadedFiles);
//     } else {
//       setFiles([]);
//     }
//   }, [value]);
//   const { getRootProps, getInputProps, isDragActive } = useDropzone({
//     onDrop,
//     accept: {
//       "image/png": [],
//       "image/jpeg": [], // covers both JPG and JPEG
//       "image/webp": [],
//       "application/pdf": [],
//       "application/msword": [], // DOC
//       "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
//         [], // DOCX
//       "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [], // XLSX
//     },
//   });
//   const handleFileRemove = (fileName: string) => {
//     const updatedFiles = files.filter((file) => file.name !== fileName);
//     setFiles(updatedFiles);
//     updateOnChange(updatedFiles);
//   };
//   return (
//     <div className="w-full">
//       <div {...getRootProps()} onClick={(e) => e.stopPropagation()}>
//         <input id={name} key={files.length} {...getInputProps()} />
//         <div className="flex w-full flex-col items-center rounded-md border border-dashed border-neutral-400 p-4">
//           <div className="inline-flex cursor-pointer flex-col items-center justify-center rounded-md px-4 py-2">
//             <IoImageOutline size={30} className="text-neutral-900" />
//             {isDragActive ? (
//               <span className="text-base">Drop the files here ...</span>
//             ) : (
//               <span className="text-base">
//                 <b className="text-info-500">Upload file(s)</b> or drag and drop
//               </span>
//             )}
//           </div>
//           <p className="text-sm text-neutral-400">
//             PNG, JPG, JPEG, WEBP, PDF, DOC, DOCX, XLSX upto 5MB
//           </p>
//         </div>
//       </div>
//       <div className="mt-4 grid w-full grid-cols-2 justify-center gap-x-11 gap-y-8">
//         {(files || (defaultValue && Array.from(defaultValue as FileList)))?.map(
//           (file) => (
//             <div
//               key={file.name}
//               className="flex w-full items-center gap-3 rounded-2xl border bg-neutral-50 px-4 py-4"
//             >
//               <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary-500">
//                 <FaRegFileLines size={16} className="text-primary-500" />
//               </div>
//               <div className="flex w-full flex-col">
//                 <ToolTipEllipsis title={file.name} className="max-w-[25ch]" />
//                 <span className="text-sm text-neutral-500">{file.size}</span>
//               </div>
//               <button
//                 type="button"
//                 className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-danger-50 hover:cursor-pointer"
//                 onClick={(e) => {
//                   e.preventDefault();
//                   handleFileRemove(file.name);
//                 }}
//               >
//                 <FaRegTrashCan size={16} className="text-danger-500" />
//               </button>
//             </div>
//           ),
//         )}
//       </div>
//     </div>
//   );
// }
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FaRegFileLines, FaRegTrashCan } from "react-icons/fa6";
import { IoImageOutline } from "react-icons/io5";
import { toast } from "sonner";
import { Attachment } from "~/lib/types";

interface FileItem {
  id: string;
  name: string;
  size: string;
  file?: File; // Optional for URL-based files
  existingFile?: Attachment; // For existing files from server
  isExisting?: boolean; // Flag to differentiate between new and existing files
}

interface Props {
  onChange: (data: {
    files: FileList | null;
    removedFiles: Attachment[];
  }) => void;
  defaultValue: Attachment[] | FileList | null; // Can be existing file objects or FileList
  name: string;
  value: Attachment[] | FileList | null;
}

export function DropZone({ onChange, defaultValue, name, value }: Props) {
  const [fileItems, setFileItems] = useState<FileItem[]>([]);
  const [removedFiles, setRemovedFiles] = useState<Attachment[]>([]);

  const getFileSize = (file: File): string => {
    return `${(file.size / 1024).toFixed(2)}KB`;
  };

  const updateOnChange = useCallback(
    (updatedItems: FileItem[], updatedRemovedFiles: Attachment[]) => {
      // Create FileList from new files only
      const dataTransfer = new DataTransfer();
      updatedItems
        .filter((item) => item.file) // Only include items with actual File objects
        .forEach((item) => dataTransfer.items.add(item.file!));

      onChange({
        files: dataTransfer.files.length > 0 ? dataTransfer.files : null,
        removedFiles: updatedRemovedFiles,
      });
    },
    [onChange],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const newItems: FileItem[] = [];
      acceptedFiles.forEach((file) => {
        if (!fileItems.find((item) => file.name === item.name)) {
          newItems.push({
            id: `file-${Date.now()}-${Math.random()}`,
            name: file.name,
            size: getFileSize(file),
            file,
            isExisting: false,
          });
        } else {
          toast.info("There already exists a file with this name");
        }
      });

      const mergedItems = [...fileItems, ...newItems];
      setFileItems(mergedItems);
      updateOnChange(mergedItems, removedFiles);
    },
    [fileItems, updateOnChange, removedFiles],
  );

  useEffect(() => {
    let items: FileItem[] = [];

    if (value) {
      if (Array.isArray(value)) {
        // Handle ExistingFile array
        items = value.map((existingFile) => ({
          id: `existing-${existingFile.id}`,
          name: existingFile.name,
          size: "", // Size unknown for existing files
          existingFile,
          isExisting: true,
        }));
      } else if (value instanceof FileList) {
        // Handle FileList
        items = Array.from(value).map((file, index) => ({
          id: `file-${index}`,
          name: file.name,
          size: getFileSize(file),
          file,
          isExisting: false,
        }));
      }
    }

    setFileItems(items);
  }, [value]);

  // Initialize with defaultValue if value is not provided
  useEffect(() => {
    if (!value && defaultValue) {
      let items: FileItem[] = [];

      if (Array.isArray(defaultValue)) {
        // Handle ExistingFile array
        items = defaultValue.map((existingFile) => ({
          id: `default-${existingFile.id}`,
          name: existingFile.name,
          size: "", // Size unknown for existing files
          existingFile,
          isExisting: true,
        }));
      } else if (defaultValue instanceof FileList) {
        // Handle FileList
        items = Array.from(defaultValue).map((file, index) => ({
          id: `default-file-${index}`,
          name: file.name,
          size: getFileSize(file),
          file,
          isExisting: false,
        }));
      }

      setFileItems(items);
    }
  }, [defaultValue, value]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/png": [],
      "image/jpeg": [],
      "image/webp": [],
      "application/pdf": [],
      "application/msword": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
    },
  });

  const handleFileRemove = (itemId: string) => {
    const itemToRemove = fileItems.find((item) => item.id === itemId);
    const updatedItems = fileItems.filter((item) => item.id !== itemId);

    const updatedRemovedFiles = [...removedFiles];

    // If removing an existing file, add it to removedFiles
    if (itemToRemove?.isExisting && itemToRemove.existingFile) {
      updatedRemovedFiles.push(itemToRemove.existingFile);
      setRemovedFiles(updatedRemovedFiles);
    }

    setFileItems(updatedItems);
    updateOnChange(updatedItems, updatedRemovedFiles);
  };

  return (
    <div className="w-full">
      <div {...getRootProps()} onClick={(e) => e.stopPropagation()}>
        <input id={name} key={fileItems.length} {...getInputProps()} />
        <div className="flex w-full flex-col items-center rounded-md border border-dashed border-neutral-400 p-4">
          <div className="inline-flex cursor-pointer flex-col items-center justify-center rounded-md px-4 py-2">
            <IoImageOutline size={30} className="text-neutral-900" />
            {isDragActive ? (
              <span className="text-base">Drop the files here ...</span>
            ) : (
              <span className="text-base">
                <b className="text-info-500">Upload file(s)</b> or drag and drop
              </span>
            )}
          </div>
          <p className="text-sm text-neutral-400">
            PNG, JPG, JPEG, WEBP, PDF, DOC, DOCX, XLSX upto 5MB
          </p>
        </div>
      </div>
      <div className="mt-4 grid w-full grid-cols-2 justify-center gap-x-11 gap-y-8">
        {fileItems.map((item) => (
          <div
            key={item.id}
            className="flex w-full items-center gap-3 rounded-2xl border bg-neutral-50 px-4 py-4"
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary-500">
              <FaRegFileLines size={16} className="text-primary-500" />
            </div>
            <div className="flex w-full flex-col">
              <span
                className="line-clamp-2 break-all text-sm font-medium leading-snug"
                title={item.name}
              >
                {item.name}
              </span>
              <span className="text-sm text-neutral-500">
                {item.size || (item.isExisting ? "Existing file" : "")}
              </span>
            </div>
            <button
              type="button"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-danger-50 hover:cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                handleFileRemove(item.id);
              }}
            >
              <FaRegTrashCan size={16} className="text-danger-500" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
